/**
 * /payroll/employees — Payroll Employee List
 *
 * Table: Name, Hire Date, Pay Type, Rate, Frequency, Status
 * "Add Employee" button opens a dialog.
 * Clicking a row navigates to /payroll/employees/:id
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { api } from "@/lib/api";
import { payrollEmployeesQueryOptions, payrollKeys } from "@/lib/queries";
import { MoneyField } from "@/components/MoneyField";
import type { PayrollEmployee } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/employees")({
  component: PayrollEmployeesPage,
});

function fmtDate(s: string) {
  return new Date(s + "T00:00:00").toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}
function fmtRate(rate: string, payType: string) {
  const n = parseFloat(rate);
  const fmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
  return payType === "hourly" ? `${fmt}/hr` : `${fmt}/yr`;
}
const FREQ_LABEL: Record<string, string> = {
  weekly: "Weekly", biweekly: "Bi-weekly", semi_monthly: "Semi-monthly", monthly: "Monthly",
};

// ─── Add Employee dialog ──────────────────────────────────────────────────────

interface AddEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const EMPTY_FORM = () => ({
  // hire_date starts blank — you're usually recording an employee who was
  // hired in the past, so defaulting to today just makes the user re-type it.
  first_name: "", last_name: "", hire_date: "",
  pay_frequency: "biweekly", pay_type: "hourly", default_rate: "",
  address_state: "", address_city: "",
});

function AddEmployeeDialog({ open, onClose, onCreated }: AddEmployeeDialogProps) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);

  // Reset form each time the dialog opens so stale data doesn't linger
  useEffect(() => {
    if (open) setForm(EMPTY_FORM);
  }, [open]);

  const mut = useMutation({
    mutationFn: (body: typeof form) =>
      api<PayrollEmployee>("/api/payroll/employees", { method: "POST", body }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: payrollKeys.employees() });
      onCreated(data.id);
    },
  });

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) =>
    setForm((f) => ({ ...f, [k]: (e.target as HTMLInputElement).value }));

  const valid = form.first_name && form.last_name && form.hire_date && form.default_rate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Employee</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField label="First Name" value={form.first_name} onChange={set("first_name")} required />
          <TextField label="Last Name" value={form.last_name} onChange={set("last_name")} required />
        </Box>
        <TextField
          label="Hire Date" type="date" value={form.hire_date} onChange={set("hire_date")}
          InputLabelProps={{ shrink: true }} required
          helperText="The employee's actual start date"
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <FormControl>
            <InputLabel>Pay Type</InputLabel>
            <Select value={form.pay_type} label="Pay Type" onChange={(e) => setForm(f => ({ ...f, pay_type: e.target.value }))}>
              <MenuItem value="hourly">Hourly</MenuItem>
              <MenuItem value="salary">Salary</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Frequency</InputLabel>
            <Select value={form.pay_frequency} label="Frequency" onChange={(e) => setForm(f => ({ ...f, pay_frequency: e.target.value }))}>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="biweekly">Bi-weekly</MenuItem>
              <MenuItem value="semi_monthly">Semi-monthly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <MoneyField
          label={form.pay_type === "hourly" ? "Hourly Rate" : "Annual Salary"}
          value={form.default_rate} onChange={(v) => setForm(f => ({ ...f, default_rate: v }))} required
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField label="City" value={form.address_city} onChange={set("address_city")} />
          <TextField label="State" value={form.address_state} onChange={set("address_state")}
            inputProps={{ maxLength: 2 }} helperText="2-letter code" />
        </Box>
        {mut.isError && (
          <Typography color="error" variant="body2">
            {String((mut.error as Error)?.message ?? "Error creating employee")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => mut.mutate(form)} disabled={mut.isPending || !valid}>
          {mut.isPending ? "Creating…" : "Add Employee"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function PayrollEmployeesPage() {
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const { data, isLoading } = useQuery(payrollEmployeesQueryOptions(!showInactive ? true : false));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Employees</Typography>
          <Box sx={{ mt: 0.5 }}>
            <HelpHint docPath="payroll-l1/employee-setup.md" label="Employee setup help" />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => setShowInactive(v => !v)}>
            {showInactive ? "Hide Inactive" : "Show Inactive"}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add Employee
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Hire Date</TableCell>
              <TableCell>Pay Type</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell>State</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((emp: PayrollEmployee) => (
              <TableRow
                key={emp.id} hover sx={{ cursor: "pointer" }}
                onClick={() => navigate({ to: "/payroll/employees/$employeeId", params: { employeeId: emp.id } })}
              >
                <TableCell sx={{ fontWeight: 500 }}>{emp.first_name} {emp.last_name}</TableCell>
                <TableCell>{fmtDate(emp.hire_date)}</TableCell>
                <TableCell sx={{ textTransform: "capitalize" }}>{emp.pay_type}</TableCell>
                <TableCell align="right">{fmtRate(emp.default_rate, emp.pay_type)}</TableCell>
                <TableCell>{FREQ_LABEL[emp.pay_frequency] ?? emp.pay_frequency}</TableCell>
                <TableCell>{emp.address_state ?? "—"}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={emp.is_active ? "Active" : "Terminated"}
                    color={emp.is_active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            {(data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No employees found. Click "Add Employee" to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <AddEmployeeDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(id) => { setAddOpen(false); navigate({ to: "/payroll/employees/$employeeId", params: { employeeId: id } }); }}
      />
    </Box>
  );
}
